(() => {
  'use strict';

  function verifyBundleCart(cart, items, bundleId, expectedUnit, currency) {
    if (cart?.currency !== currency || currency !== 'USD' || !Array.isArray(cart.items)) return false;
    const requested = new Map(items.map(item => [`${item.id}-${item.selling_plan || 0}`, item]));
    const remaining = new Map(items.map(item => [`${item.id}-${item.selling_plan || 0}`, item.quantity]));
    if (requested.size !== items.length || !Number.isSafeInteger(expectedUnit) || expectedUnit <= 0) return false;
    const lines = cart.items.filter(item => item.properties?._bundle === bundleId);
    const matches = lines.every(item => {
      const key = `${item.variant_id || item.id}-${item.selling_plan_allocation?.selling_plan?.id || 0}`;
      const source = requested.get(key);
      if (!source || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !Number.isSafeInteger(item.final_line_price)) return false;
      if (!Object.entries(source.properties || {}).every(([key,value]) => (item.properties || {})[key] === value)) return false;
      const free = source.properties?._free === '1';
      if ((item.properties?._free === '1') !== free || item.final_line_price !== (free ? 0 : expectedUnit * item.quantity)) return false;
      const left = remaining.get(key) - item.quantity;
      remaining.set(key, left);
      return left >= 0;
    });
    return matches && [...remaining.values()].every(n => n === 0);
  }
  async function addBundleSafely({items, bundleId, currency, expectedUnit, cartUrl, addUrl, notify = () => {}}) {
    if (currency !== 'USD' || !items.length || items.some(item => !Number.isSafeInteger(Number(item.id)) || Number(item.id) <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.properties?._bundle !== bundleId)) throw new Error('This bundle is not available. Please review your selection.');
    let success = false, rollbackFailed = false;
    const json = async (url, options) => {
      const response = await fetch(url, options);
      let data;
      try { data = await response.json(); } catch (_) { throw new Error('The cart could not be verified. Please try again.'); }
      if (!response.ok) throw new Error(data.description || data.message || 'The bundle could not be added. Please try again.');
      return data;
    };
    const readCart = () => json(cartUrl + '.js', {headers:{Accept:'application/json'}, cache:'no-store'});
    const post = data => ({method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify(data)});
    notify({id:bundleId, phase:'start'});
    try {
      await json(addUrl + '.js', post({items}));
      const cart = await readCart();
      if (!verifyBundleCart(cart, items, bundleId, expectedUnit, currency)) throw new Error('The bundle price or gifts could not be verified. Please try again.');
      success = true;
      return cart;
    } catch (error) {
      try {
        const cart = await readCart();
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
        const updates = Object.fromEntries(cart.items.filter(item => item.properties?._bundle === bundleId).map(item => [item.key, 0]));
        if (Object.keys(updates).length) {
          await json(cartUrl + '/update.js', post({updates}));
          const clean = await readCart();
          if (!Array.isArray(clean.items) || clean.items.some(item => item.properties?._bundle === bundleId)) throw new Error('Incomplete rollback');
        }
      } catch (_) { rollbackFailed = true; }
      if (rollbackFailed) throw new Error('This bundle could not be verified. Review your cart before checkout.');
      throw error;
    } finally { notify({id:bundleId, phase:'end', success, rollbackFailed}); }
  }

  const text = (config, key, values = {}) => Object.entries(values).reduce((s, [k, v]) => s.replaceAll('{{ '+k+' }}', String(v)).replaceAll('__'+k+'__',String(v)), config.ui?.[key] || key);
  const same = (a, b) => String(a) === String(b);
  const sampleCount = (config, state) => config.sampleCounts[state.mode];
  function selection(config, state) {
    if (config.currency !== 'USD') throw new Error('This offer is available in USD only.');
    if (!['once','sub'].includes(state.mode) || ![2,4,6,8].includes(state.weeks)) throw new Error(text(config, 'unavailable'));
    if (state.mode === 'once') {
      if (!config.once?.id || !config.once.available) throw new Error('This bundle is not available for purchase yet.');
      if (config.once.price !== config.prices.once) throw new Error('These bundle prices are not active yet.');
      return {id: config.once.id, quantity: 1};
    }
    const variant = config.gifts.find(v => same(v.id, state.gift));
    if (!variant) throw new Error('Choose your free scent to continue.');
    const plan = variant.plans.find(p => p.appId === 'appstle' && (p.weeks === state.weeks || p.name === `Every ${state.weeks} weeks`) && p.price === config.prices.sub);
    if (!variant.available) throw new Error('This scent is unavailable. Choose another free scent.');
    if (!plan || !config.subscriptionOnly) throw new Error('Subscriptions for this bundle are not active yet.');
    return {id: variant.id, quantity: 1, selling_plan: plan.id};
  }
  function samplePlan(config, state, sample) {
    if (!sample?.id || !sample.handle || !sample.available) throw new Error(text(config, 'samples_unavailable'));
    return null; // The sample is included in the paid parent, not a separately subscribed SKU.
  }
  function buildItems(config, state, bundleId) {
    const item = selection(config, state), count = sampleCount(config, state);
    const chosen = Array.from({length:count}, (_, i) => config.samples.find(s => same(s.id, state.samples[i])));
    if (chosen.some(s => !s)) throw new Error(text(config, 'missing_samples'));
    const properties = {_bundle:bundleId, _curated_bundle:config.handle};
    item.properties = {...properties, 'Bundle includes':config.contents, 'Purchase option':state.mode === 'sub' ? 'Subscription · 4 + 1 FREE' : 'One-time · 4 products'};
    if (state.mode === 'sub') item.properties['Free product every delivery'] = config.gifts.find(v => same(v.id, state.gift)).name;
    if (count) {
      chosen.forEach(sample => samplePlan(config, state, sample));
      item.properties._hs_sample_preferences = JSON.stringify(chosen.map(sample => sample.handle));
      item.properties['Included sample scents'] = chosen.map(sample => sample.name).join(', ');
    }
    return [item];
  }

  function verifiedCart(cart, items, bundleId, expected, currency) {
    return verifyBundleCart(cart, items, bundleId, expected, currency);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {selection, sampleCount, buildItems, verifiedCart, verifyBundleCart, addBundleSafely};
  if (typeof document === 'undefined') return;
  const money = n => new Intl.NumberFormat('en-US', {style:'currency',currency:'USD'}).format(n / 100);
  const escape = value => String(value).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  document.querySelectorAll('[data-curated-bundle]').forEach(root => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const config = JSON.parse(root.querySelector('[data-cb-config]').textContent);
    const state = {mode:'sub', weeks:4, gift:null, samples:[null,null]};
    const q = s => root.querySelector(s);
    const qa = s => Array.from(root.querySelectorAll(s));
    const t = (key, values) => text(config, key, values);
    const error = q('[data-cb-error]'), button = q('[data-cb-add]'), dialog = q('[data-cb-sample-picker]');
    let busy = false, sampleSlot = 0, opener;
    const imageMarkup = sample => `<img src="${escape(sample.image)}&width=200" alt="" width="100" height="100" loading="lazy">`;
    function render() {
      const sub = state.mode === 'sub', total = config.prices[state.mode], count = sub ? 5 : 4, gifts = sampleCount(config, state);
      qa('[data-cb-mode]').forEach(b => {const on=b.dataset.cbMode===state.mode;b.classList.toggle('on',on);b.setAttribute('aria-pressed',String(on));});
      qa('[data-sub-only]').forEach(el => {el.hidden=!sub;});
      qa('[data-once-only]').forEach(el => {el.hidden=sub;});
      q('[data-cb-price]').textContent=money(total);
      q('[data-cb-unit]').textContent=money(Math.round(total/count))+' / '+config.unit;
      q('[data-cb-count]').textContent=count+' '+config.label.toLowerCase()+' total';
      q('[data-cb-cadence]').textContent=sub?'USD every '+state.weeks+' weeks':'USD · one-time purchase';
      q('[data-cb-recap]').textContent=sub?'4 favorites + 1 FREE of your choice':'4 favorites';
      qa('[data-cb-gift]').forEach(b=>{const on=same(b.dataset.cbGift,state.gift);b.classList.toggle('on',on);b.setAttribute('aria-pressed',String(on));});
      q('[data-cb-samples]').hidden = gifts === 0;
      q('[data-cb-sample-heading]').textContent = t(gifts === 1 ? 'sample_heading_one' : 'sample_heading_two');
      qa('[data-cb-sample-slot]').forEach((slot, i) => {
        const sample = config.samples.find(s => same(s.id, state.samples[i]));
        slot.hidden = i >= gifts;
        slot.classList.toggle('is-picked', !!sample);
        slot.querySelector('[data-cb-sample-visual]').innerHTML = sample ? imageMarkup(sample) : '<span aria-hidden="true">+</span>';
        const nameEl = slot.querySelector('[data-cb-sample-name]');
        nameEl.textContent = sample?.name || t('choose');
        if (sample?.inspiration) { const sm = document.createElement('span'); sm.className = 'ns-smells'; const b = document.createElement('b'); b.textContent = sample.inspiration; sm.append('Smells like ', b); nameEl.append(sm); }
        slot.setAttribute('aria-label',t(sample ? 'sample_selected' : 'sample_slot',{number:i+1,name:sample?.name}));
      });
      let valid=true, unavailable='';
      try {
        if (state.mode === 'once' || state.gift) {
          const parent = selection(config,state);
          if (gifts && !config.samples.some(s => {try {samplePlan(config,state,s,parent); return true;} catch (_) {return false;}})) {valid=false;unavailable=t('samples_unavailable');}
          state.samples.slice(0,gifts).filter(Boolean).forEach(id => samplePlan(config,state,config.samples.find(s => same(s.id,id)),parent));
        }
      } catch (e) {valid=false;unavailable=e.message;}
      button.disabled=busy || !valid;
      const missing = Array.from({length:gifts}, (_, i) => state.samples[i]).some(id => !id);
      button.textContent=busy?t('adding'):sub&&!state.gift?'Choose your FREE scent':missing?t(gifts===1?'sample_heading_one':'choose_samples'):t(sub?'subscribe':'add',{price:money(total)});
      const gift=config.gifts.find(v=>same(v.id,state.gift));
      q('[data-cb-picked]').textContent=gift?'Your FREE '+config.unit+': '+gift.name:'Pick any scent below';
      error.textContent=valid?'':unavailable;
      qa('[data-cb-mode], [data-cb-gift], [data-cb-sample-slot], [data-cb-frequency]').forEach(control=>{control.disabled=busy || (control.hasAttribute('data-cb-gift') && !config.gifts.find(v=>same(v.id,control.dataset.cbGift))?.available);});
    }
    function openSamples(index) {
      sampleSlot = index; opener = q(`[data-cb-sample-slot="${index}"]`);
      q('[data-cb-picker-title]').textContent = t('picker_heading',{number:index+1});
      let parent;
      try { parent=selection(config,state); } catch (_) {parent=null;}
      q('[data-cb-sample-options]').innerHTML=config.samples.map(sample=>{
        let available = sample.available && !!sample.handle;
        if (parent) {try {samplePlan(config,state,sample,parent);} catch (_) {available=false;}}
        const on = same(sample.id,state.samples[index]);
        return `<button type="button" data-cb-sample-choice="${sample.id}" aria-label="${escape(t('choose_scent',{name:sample.name})+(sample.inspiration?' — '+t('smells_like')+' '+sample.inspiration:''))}" aria-pressed="${on}"${available?'':' disabled'}>${imageMarkup(sample)}<span>${escape(sample.name)}</span>${sample.inspiration?`<small class="cb-inspiration">${escape(t('smells_like'))}<br>${escape(sample.inspiration)}</small>`:''}${on?'<b aria-hidden="true">✓</b>':''}</button>`;
      }).join('');
      dialog.showModal();
    }
    qa('[data-cb-mode]').forEach(b=>b.addEventListener('click',()=>{if(busy)return;state.mode=b.dataset.cbMode;render();}));
    q('[data-cb-frequency]').addEventListener('change',e=>{if(busy)return;state.weeks=Number(e.target.value);render();});
    qa('[data-cb-gift]').forEach(b=>b.addEventListener('click',()=>{if(busy)return;state.gift=b.dataset.cbGift;render();}));
    qa('[data-cb-sample-slot]').forEach(b=>b.addEventListener('click',()=>{if(!busy)openSamples(Number(b.dataset.cbSampleSlot));}));
    q('[data-cb-picker-close]').addEventListener('click',()=>dialog.close());
    dialog.addEventListener('close',()=>{if(opener?.isConnected)opener.focus({preventScroll:true});});
    dialog.addEventListener('click',e=>{
      const choice=e.target.closest('[data-cb-sample-choice]');
      if(choice&&!choice.disabled){state.samples[sampleSlot]=choice.dataset.cbSampleChoice;render();dialog.close();}
      else if(e.target===dialog){const r=dialog.getBoundingClientRect();if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)dialog.close();}
    });
    qa('[data-cb-gallery]').forEach(b=>b.addEventListener('click',()=>{
      const main=q('[data-cb-main]'),url=new URL(b.dataset.cbGallery,location.href);
      const atWidth=width=>{url.searchParams.set('width',width);return url.href;};
      main.srcset=[360,540,720,900,1200].map(width=>atWidth(width)+' '+width+'w').join(', ');
      main.src=atWidth(720);main.alt=b.dataset.alt;
      qa('[data-cb-gallery]').forEach(x=>x.setAttribute('aria-pressed',String(x===b)));
    }));
    button.addEventListener('click',async()=>{
      if(busy)return;
      if(state.mode==='sub'&&!state.gift){q('[data-cb-gifts]').scrollIntoView({behavior:'smooth',block:'center'});q('[data-cb-gift]')?.focus();error.textContent='Choose your free scent first.';return;}
      const missing=Array.from({length:sampleCount(config,state)},(_,i)=>state.samples[i]).findIndex(id=>!id);
      if(missing!==-1){openSamples(missing);return;}
      const bundleId='curated-'+crypto.randomUUID();
      let items;
      try {items=buildItems(config,state,bundleId);}catch(e){error.textContent=e.message;return;}
      busy=true;render();
      try {
        await addBundleSafely({
          items, bundleId, currency:config.currency, expectedUnit:config.prices[state.mode],
          cartUrl:config.cartUrl, addUrl:config.addUrl,
          notify:detail => document.dispatchEvent(new CustomEvent('hs:bundle-transaction',{detail}))
        });
        const cartLink=q('[data-cb-cart]');
        if(cartLink){cartLink.click();button.textContent=t('added');}else window.location.assign(config.cartUrl);
      }catch(e){error.textContent=e.message;}
      finally{busy=false;const message=error.textContent;render();error.textContent=message;button.textContent=t(message?'retry':'added');}
    });
    render();
  });
})();



